import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SignIn } from "@clerk/clerk-react";
import { clerkEnabled } from "@/lib/clerk";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Activity, ArrowRight } from "lucide-react";

const DEMO_ACCOUNTS = [
  { label: "Commercial", email: "commercial@isale.demo", role: "commercial", name: "Alex Commercial" },
  { label: "Manager", email: "manager@isale.demo", role: "manager", name: "Sam Manager" },
  { label: "Admin", email: "admin@isale.demo", role: "admin", name: "Jordan Admin" },
];

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("commercial");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register({ email, password, name, role });
      toast.success("Authenticated", { description: "Welcome to iSale." });
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const quickDemo = async (acc) => {
    setLoading(true);
    try {
      try {
        await login(acc.email, "demo1234");
      } catch {
        await register({
          email: acc.email, password: "demo1234",
          name: acc.name, role: acc.role,
        });
      }
      toast.success(`Logged in as ${acc.label}`);
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create demo account");
    } finally {
      setLoading(false);
    }
  };

  // Founding Beta: managed auth via Clerk (invitation-only — sign-up is
  // disabled in the Clerk dashboard, so only invited beta members get in)
  if (clerkEnabled) {
    return (
      <div className="min-h-screen grid md:grid-cols-2 bg-white" data-testid="login-page">
        <div className="flex items-center justify-center p-8 md:p-16 order-2 md:order-1">
          <div className="w-full max-w-sm">
            <div className="flex items-center gap-2 mb-10">
              <Activity className="w-5 h-5" strokeWidth={2} />
              <span className="font-heading font-bold text-lg tracking-tight">
                iSale<span className="text-gray-400">/V3</span>
              </span>
              <span className="overline text-gray-400 ml-auto">Founding Beta</span>
            </div>
            <SignIn routing="hash" forceRedirectUrl="/" />
          </div>
        </div>
        <div
          className="hidden md:block order-1 md:order-2 bg-gray-900 relative"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1630449255710-fee6f188bad1?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(1)",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
          <div className="absolute bottom-10 left-10 right-10 text-white">
            <div className="overline text-white/60 mb-3">Contradiction Engine · v3.1</div>
            <p className="font-heading text-2xl font-medium leading-snug max-w-md">
              “Most deals die not from lack of signal, but from lack of listening.”
            </p>
            <div className="mt-4 text-xs text-white/70 font-mono">— iSale Engineering, 2026</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white" data-testid="login-page">
      <div className="flex items-center justify-center p-8 md:p-16 order-2 md:order-1">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-10">
            <Activity className="w-5 h-5" strokeWidth={2} />
            <span className="font-heading font-bold text-lg tracking-tight">
              iSale<span className="text-gray-400">/V3</span>
            </span>
            <span className="overline text-gray-400 ml-auto">Passive Mode</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-3">
            {mode === "login" ? "Sign in." : "Create account."}
          </h1>
          <p className="text-sm text-gray-500 mb-10 leading-relaxed">
            Contradiction Engine for sales reality. No sliders. No dashboards to open.
            It watches your pipeline in silence — and speaks only when the math disagrees.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <>
                <div>
                  <Label htmlFor="name" className="overline text-gray-500 mb-2 block">Name</Label>
                  <Input
                    id="name" value={name} onChange={(e) => setName(e.target.value)} required
                    data-testid="register-name" className="rounded-sm h-10"
                  />
                </div>
                <div>
                  <Label className="overline text-gray-500 mb-2 block">Role</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger className="rounded-sm h-10" data-testid="register-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div>
              <Label htmlFor="email" className="overline text-gray-500 mb-2 block">Email</Label>
              <Input
                id="email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} required
                data-testid="auth-email" className="rounded-sm h-10"
              />
            </div>
            <div>
              <Label htmlFor="password" className="overline text-gray-500 mb-2 block">Password</Label>
              <Input
                id="password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required minLength={6}
                data-testid="auth-password" className="rounded-sm h-10"
              />
            </div>
            <Button
              type="submit" disabled={loading} data-testid="auth-submit"
              className="w-full h-10 rounded-sm bg-black hover:bg-gray-800 text-white gap-2"
            >
              {loading ? "..." : mode === "login" ? "Continue" : "Create account"}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </form>

          <div className="my-8 flex items-center gap-3 text-xs text-gray-400">
            <div className="h-px bg-gray-200 flex-1" />
            <span className="overline">Quick demo</span>
            <div className="h-px bg-gray-200 flex-1" />
          </div>

          <div className="grid grid-cols-3 gap-2">
            {DEMO_ACCOUNTS.map((acc) => (
              <button
                key={acc.role}
                onClick={() => quickDemo(acc)}
                disabled={loading}
                data-testid={`demo-${acc.role}-btn`}
                className="border border-gray-200 hover:border-black hover:bg-black hover:text-white transition-colors px-3 py-3 rounded-sm text-left"
              >
                <div className="overline text-gray-400">{acc.label}</div>
                <div className="text-xs font-medium mt-1 truncate">{acc.name}</div>
              </button>
            ))}
          </div>

          <button
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="mt-8 text-sm text-gray-500 hover:text-black"
            data-testid="toggle-auth-mode"
          >
            {mode === "login" ? "New here? Create account" : "Already have an account? Sign in"}
          </button>
        </div>
      </div>
      <div
        className="hidden md:block order-1 md:order-2 bg-gray-900 relative"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1630449255710-fee6f188bad1?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "grayscale(1)",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="overline text-white/60 mb-3">Contradiction Engine · v3.1</div>
          <p className="font-heading text-2xl font-medium leading-snug max-w-md">
            “Most deals die not from lack of signal, but from lack of listening.”
          </p>
          <div className="mt-4 text-xs text-white/70 font-mono">— iSale Engineering, 2026</div>
        </div>
      </div>
    </div>
  );
}
